/* eslint-disable jsx-a11y/media-has-caption */
import clsx from 'clsx'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import Pane from '../Pane'
import { detectGiftCardNumber } from 'api/promo'
import ListItem from 'components/Luxkit/List/ListItem'
import LineKeyboardAltIcon from 'components/Luxkit/Icons/line/LineKeyboardAltIcon'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import LineCameraIcon from 'components/Luxkit/Icons/line/LineCameraIcon'
import useToggle from 'hooks/useToggle'
import LoadingIndicator from '../Loading/LoadingIndicator'

const Root = styled(Pane)`
  background-color: black;

  ${mediaQueryDown.mobile} {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`

const HeadingContainer = styled.div`
  display: flex; 
  flex-direction: row; 
  justify-content: space-between;
  padding: ${rem(20)}; 
  padding-left: ${rem(30)}; 
`

const StyledHeading = styled(Heading)`
  line-height: ${rem(40)};
`

const MessageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  ${mediaQueryUp.tablet} {
    height: ${rem(300)};
  }
`

const VideoContainer = styled.div`
  position: relative;
  display: none;
  flex: 1;

  &.show {
    display: flex;
    align-items: center;
  }
`
const Video = styled.video`
  max-width: 100%;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-around;
  position: absolute;
  bottom: ${rem(20)};
  width: 100%;
`

const flashEffect = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`

const ProcessingImageContainer = styled.div`
  width: 100%;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    animation: ${flashEffect} 0.5s ease-out forwards;
    z-index: 1;
  }
`

const LoadingIndicatorContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ProcessingImage = styled.img`
  width: 100%;
`

const Footer = styled.div`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} ${rem(24)} ${rem(24)};
  }
`

const TransparentListItem = styled(ListItem)`
  background-color: transparent;
`

interface Props {
  onSuccess: (code: string, pin: string | undefined) => void
  onCancel: () => void
}

function ScanCode({ onSuccess, onCancel }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied'>('prompt')
  const { value: isError, on: setError, off: clearError } = useToggle()
  const [processingImage, setProcessingImage] = useState<string | null>(null)

  // const getDeviceOptions = async() => {
  //   try {
  //     const devices = await navigator.mediaDevices.enumerateDevices()
  //     console.log('Devices:', devices)
  //     setPermissionState('granted')
  //     return true
  //   } catch (e) {
  //     setPermissionState('denied')
  //     return false
  //   }
  // }

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => {
      track.stop()
    })
  }

  const startCamera = async() => {
    try {
      stopCamera()
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      streamRef.current = stream
      setPermissionState('granted')
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
      // console.log('Tracks:', stream.getTracks())
      return true
    } catch (e) {
      setPermissionState('denied')
      return false
    }
  }

  useEffect(() => {
    startCamera()

    return stopCamera
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleImage = async(dataURL: string) => {
    const imageBase64 = dataURL.replace(/^.*?,/, '')
    setProcessingImage(dataURL)
    const result = await detectGiftCardNumber(imageBase64)
    setProcessingImage(null)
    if (result.status === 'success') {
      onSuccess(result.cardNumber, result.accessCode)
    } else {
      setError()
    }
  }

  const takePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas')
      const video = videoRef.current
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const context = canvas.getContext('2d')

      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height)
        const dataURL = canvas.toDataURL('image/png')
        handleImage(dataURL)
      }
    }
  }

  return (
    <Root paletteType="inverse">
      <HeadingContainer>
        <StyledHeading variant="heading4" colour="neutral-one"> Add gift card</StyledHeading>

        <IconButton kind="tertiary" onClick={onCancel}>
          <LineTimesIcon colour="neutral-one"/>
        </IconButton>
      </HeadingContainer>

      {permissionState === 'prompt' && (
        <MessageContainer>
          <BodyText variant="large">Waiting for camera access...</BodyText>
        </MessageContainer>
      )}
      {permissionState === 'denied' && (
        <MessageContainer>
          <BodyText variant="large">Camera access required</BodyText>
        </MessageContainer>
      )}
      {isError && (
        <MessageContainer>
          <BodyText variant="large" colour="neutral-one">Unable to scan gift card details</BodyText>
        </MessageContainer>
      )}

      <VideoContainer className={clsx({ show: permissionState === 'granted' && !isError && !processingImage })}>
        <Video ref={videoRef} autoPlay />

        <ButtonContainer>
          <IconButton kind="primary" onClick={takePhoto} shape="circle" size="large">
            <LineCameraIcon />
          </IconButton>
        </ButtonContainer>
      </VideoContainer>

      {processingImage && <ProcessingImageContainer>
        <ProcessingImage src={processingImage} alt="" />
        <LoadingIndicatorContainer>
          <LoadingIndicator inline />
        </LoadingIndicatorContainer>
      </ProcessingImageContainer>}

      <Footer>
        {isError && <TransparentListItem
          startIcon={<LineCameraIcon colour="neutral-one" />}
          endIcon={<LineArrowRightIcon colour="neutral-one" />}
          onClick={clearError}
          title="Try again"
        />}
        <TransparentListItem
          startIcon={<LineKeyboardAltIcon colour="neutral-one" />}
          endIcon={<LineArrowRightIcon colour="neutral-one" />}
          onClick={onCancel}
          title="Enter details manually"
        />
      </Footer>
    </Root>
  )
}

export default ScanCode
